/**
 * US phone input with a live mask.
 *
 *   <PhoneInput label="Phone" value={phone} onChange={setPhone} required />
 *
 * The user types digits; the parentheses and hyphen appear around them and
 * behave as scenery — you cannot type them, and backspacing "through" one
 * deletes the digit before it rather than appearing to do nothing.
 *
 * ## The value contract
 *
 * `value` and `onChange` speak **digits only** (`"7175558842"`). The mask is
 * display; it never reaches the parent, the API, or the database.
 *
 * That is the whole point. `memberships.phone` is 74% formatted today because
 * forms submitted whatever the box displayed, which is also why phone search
 * silently matched nothing for most members until 2026-08-19. A component that
 * shows "(717) 555-8842" and emits "(717) 555-8842" would rebuild the problem
 * we just spent a migration undoing.
 *
 * ## Why a value-mask and not a ghost overlay
 *
 * An overlay (digits in the input, separators painted behind) keeps the value
 * clean by construction, but depends on the painted text and the real text
 * lining up to the pixel across fonts, zoom levels and browser autofill
 * styling. When it drifts it drifts silently and looks broken. A value-mask
 * needs careful caret handling — done here, and tested — and then behaves the
 * same everywhere.
 *
 * ## What it does not do
 *
 * A mask is not validation. Paste, autofill, password managers and the public
 * API all set values without typing. `utils/phoneFormat` validates on submit
 * and the backend validates again. And none of the three catch a transposed
 * ten-digit number, which is valid and belongs to a stranger.
 */

import React, { forwardRef, useCallback, useId, useLayoutEffect, useRef } from "react";
import { useTheme } from "../../context/ThemeContext";
import { getInputStyles } from "../../utils/themeUtils";
import { TOKENS, type InputSize } from "../../utils/designTokens";
import Label from "./Label";
import {
  digitsOf,
  toNationalDigits,
  formatPartialNational,
} from "../../utils/phoneFormat";

interface PhoneInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "size" | "value" | "onChange" | "type"
  > {
  /** Digits only, e.g. "7175558842". Anything else is reduced to its digits. */
  value: string;
  /** Receives digits only — never the masked display string. */
  onChange: (digits: string) => void;
  label?: React.ReactNode;
  size?: InputSize;
  error?: string;
  hint?: string;
  requiredMarker?: boolean;
  /**
   * Icon inside the control at its leading edge, as on Input. Without it a
   * phone field next to icon-adorned Inputs (the new-lead form) had to stay
   * hand-rolled.
   */
  leadingIcon?: React.ReactNode;
}

/**
 * How a saved number is shown before anyone edits it. A full number gets the
 * usual mask; a seven-digit local number reads "555-0199"; anything else
 * incomplete is shown as saved, so staff see what is really on file.
 */
export function displayStoredPhone(value: unknown, digits: string): string {
  if (digits.length === 0 || digits.length === 10) return formatPartialNational(digits);
  if (digits.length === 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return String(value ?? "").trim();
}

/**
 * Index in `display` that sits immediately after its `n`th digit.
 * Used to put the caret back where the user's digit was after re-masking.
 */
function caretAfterNthDigit(display: string, n: number): number {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < display.length; i += 1) {
    if (/\d/.test(display[i])) {
      seen += 1;
      if (seen === n) return i + 1;
    }
  }
  return display.length;
}

/**
 * Forwards a ref to the underlying input while keeping the internal one the
 * caret restoration needs. Callers that manage focus (the anon-chat field
 * advances focus between questions) cannot use a primitive that swallows the
 * ref, so this is what makes it a drop-in replacement everywhere.
 */
const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(({
  value,
  onChange,
  label,
  size = "md",
  error,
  hint,
  requiredMarker,
  required,
  id: providedId,
  className = "",
  disabled,
  leadingIcon,
  ...rest
}, forwardedRef) => {
  const { isDarkMode } = useTheme();
  const generatedId = useId();
  const id = providedId || generatedId;
  const inputRef = useRef<HTMLInputElement>(null);
  /** Digits-before-caret to restore after the masked re-render. */
  const caretDigitsRef = useRef<number | null>(null);

  /** The digits this input last emitted; anything else arrived from outside. */
  const lastEmittedRef = useRef<string | null>(null);

  const digits = toNationalDigits(value);
  // The partial mask is for a number being TYPED. A saved number that is not
  // a full ten digits is shown as it is: a stored "555-0199" was displayed as
  // "(555) 019-9", inventing an area code out of its first three digits
  // (2026-10-09). Once the person types, the mask takes over as before.
  const typedHere = lastEmittedRef.current !== null && lastEmittedRef.current === digits;
  const display = typedHere ? formatPartialNational(digits) : displayStoredPhone(value, digits);

  // Caret restoration must happen before paint, or the cursor visibly jumps
  // to the end on every keystroke that crosses a separator.
  useLayoutEffect(() => {
    const el = inputRef.current;
    const target = caretDigitsRef.current;
    caretDigitsRef.current = null;
    if (!el || target === null || document.activeElement !== el) return;
    const pos = caretAfterNthDigit(el.value, target);
    el.setSelectionRange(pos, pos);
  });

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const el = e.target;
      const nextDisplay = el.value;
      const caret = el.selectionStart ?? nextDisplay.length;

      const rawDigits = digitsOf(nextDisplay);
      let nextDigits = toNationalDigits(nextDisplay);
      let digitsBeforeCaret = digitsOf(nextDisplay.slice(0, caret)).length;

      // toNationalDigits may have shed a pasted country code; the caret index
      // was counted against the raw digits, so shift it to match.
      if (rawDigits.length > nextDigits.length && rawDigits.startsWith("1")) {
        digitsBeforeCaret = Math.max(0, digitsBeforeCaret - (rawDigits.length - nextDigits.length));
      }

      // Deleting a separator alone changes nothing about the digits, so the
      // re-mask would put the character straight back and the key would look
      // dead. Treat it as deleting the digit in front of it, which is what the
      // user meant by pressing backspace there.
      const isDeletion = nextDisplay.length < display.length;
      if (isDeletion && nextDigits === digits && digitsBeforeCaret > 0) {
        nextDigits =
          digits.slice(0, digitsBeforeCaret - 1) + digits.slice(digitsBeforeCaret);
        digitsBeforeCaret -= 1;
      }

      caretDigitsRef.current = digitsBeforeCaret;
      lastEmittedRef.current = nextDigits;
      onChange(nextDigits);
    },
    [digits, display.length, onChange]
  );

  const describedBy: string[] = [];
  if (error) describedBy.push(`${id}-error`);
  else if (hint) describedBy.push(`${id}-hint`);

  const inputClasses = [
    TOKENS.input.base,
    TOKENS.input.sizes[size],
    getInputStyles(isDarkMode),
    error ? "border-red-500 focus:ring-red-500" : "",
    leadingIcon ? "pl-10" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const control = (
    <input
      {...rest}
      ref={(el) => {
        (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
        if (typeof forwardedRef === "function") forwardedRef(el);
        else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
        }
      }}
      id={id}
      // `tel` + `tel` numeric keypad on mobile; `autoComplete="tel"` so
      // browsers and password managers can fill it. Their injected value
      // arrives through handleChange like any other input and gets masked.
      type="tel"
      inputMode="tel"
      autoComplete={rest.autoComplete ?? "tel"}
      value={display}
      onChange={handleChange}
      disabled={disabled}
      required={required}
      placeholder={rest.placeholder ?? "(555) 555-5555"}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy.length ? describedBy.join(" ") : undefined}
      className={inputClasses}
    />
  );

  return (
    <div>
      {label && (
        <Label htmlFor={id} required={requiredMarker ?? required}>
          {label}
        </Label>
      )}
      {leadingIcon ? (
        <div className="relative">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 flex items-center text-gray-400 dark:text-gray-500"
          >
            {leadingIcon}
          </span>
          {control}
        </div>
      ) : (
        control
      )}
      {error ? (
        <p id={`${id}-error`} className="text-red-500 text-xs mt-1">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs mt-1 text-gray-500 dark:text-gray-400">
          {hint}
        </p>
      ) : null}
    </div>
  );
});

PhoneInput.displayName = "PhoneInput";

export default PhoneInput;
